import {
  FC,
  useState,
  useCallback,
  memo,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../utils";
import { Button } from "../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  LogOut,
  LayoutDashboard,
  Settings,
  FolderOpenDot,
  Pickaxe,
} from "lucide-react";
import {
  useFeatureFlagWithDefault,
  FEATURE_FLAGS,
} from "@/hooks/useFeatureFlag";

interface NavItem {
  title: string;
  icon: React.FC<{ className?: string }>;
  href?: string;
  path?: string;
  isActive?: boolean;
  disabled?: boolean;
}

const Sidebar: FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const expandTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { isEnabled: cloudBuilderEnabled } = useFeatureFlagWithDefault(
    FEATURE_FLAGS.CLOUD_BUILDER
  );
  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
      if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    };
  }, []);

  const expandSidebar = useCallback(() => {
    if (isExpanded || expandTimeoutRef.current) return; // debounce rapid events

    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }

    expandTimeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
      expandTimeoutRef.current = null;
    }, 100);
  }, [isExpanded]);

  const collapseSidebar = useCallback(() => {
    if (!isExpanded || collapseTimeoutRef.current) return; // debounce rapid events

    if (expandTimeoutRef.current) {
      clearTimeout(expandTimeoutRef.current);
      expandTimeoutRef.current = null;
    }

    collapseTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
      collapseTimeoutRef.current = null;
    }, 300);
  }, [isExpanded]);

  const renderNavItem = useCallback(
    (item: NavItem, index: number) => {
      const target = item.href || item.path || "/";
      const isActive =
        target === "/"
          ? location.pathname === "/"
          : location.pathname.startsWith(target);
      const Icon = item.icon;
      const transitionDelayMs = 50 + index * 25;

      return (
        <div
          key={item.title}
          className={cn(
            "transition-transform duration-200",
            isExpanded ? "translate-x-0" : "-translate-x-1"
          )}
          style={{ transitionDelay: `${transitionDelayMs}ms` }}
        >
          {isExpanded ? (
            <Button
              variant={isActive ? "secondary" : "ghost"}
              className={cn(
                "w-full justify-start gap-2 px-3 h-10",
                isActive ? "font-medium" : ""
              )}
              asChild
            >
              <Link to={item.href || item.path || "#"}>
                <Icon
                  className={cn(
                    "h-5 w-5 transition-colors duration-200",
                    isActive ? "text-primary" : "text-gray-500"
                  )}
                />
                <span
                  className={cn(
                    "text-sm transition-colors duration-200",
                    isActive ? "text-foreground" : "text-gray-500"
                  )}
                >
                  {item.title}
                </span>
              </Link>
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    size="sm"
                    className="h-10 w-10 p-0"
                    asChild
                  >
                    <Link to={item.href || item.path || "#"}>
                      <Icon
                        className={cn(
                          "h-5 w-5 transition-colors duration-200",
                          isActive ? "text-primary" : "text-gray-500"
                        )}
                      />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{item.title}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    },
    [isExpanded, location.pathname]
  );

  // Define nav items once
  const allNavItems: NavItem[] = useMemo(
    () => [
      {
        title: "Dashboard",
        icon: LayoutDashboard,
        href: "/",
      },
      {
        title: "Build",
        icon: Pickaxe,
        href: "/chat-dashboard",
      },
      {
        title: "Projects",
        icon: FolderOpenDot,
        href: "/projects",
      },
      {
        title: "Settings",
        icon: Settings,
        href: "/settings",
      },
    ],
    []
  );

  const navItemsFiltered = useMemo(
    () =>
      cloudBuilderEnabled
        ? allNavItems
        : allNavItems.filter((item) => item.title !== "Dashboard"),
    [cloudBuilderEnabled, allNavItems]
  );

  return (
    <div
      onMouseEnter={expandSidebar}
      onMouseLeave={collapseSidebar}
      className={cn(
        "flex-shrink-0 h-full flex flex-col justify-between border-r border-muted bg-background shadow-sm",
        "transition-all duration-300 ease-out transform-gpu will-change-transform will-change-width",
        isExpanded ? "w-60" : "w-14"
      )}
    >
      <div className="flex flex-col gap-3 w-full pt-4">
        {/* Logo & Title */}
        <div className="flex items-center w-full pb-3 px-3">
          <div
            className={cn(
              "flex items-center justify-center h-5 w-5 transition-transform duration-200",
              isExpanded ? "" : "mx-auto"
            )}
          >
            <img
              src="/Logo.svg"
              alt="Logo"
              className="h-5 w-5 object-contain"
            />
          </div>
          <div
            className={cn(
              "ml-2 overflow-hidden transition-all duration-300 ease-out",
              isExpanded ? "w-auto opacity-100" : "w-0 opacity-0"
            )}
          >
            <span className="font-medium text-foreground text-sm whitespace-nowrap">
              LeapFrog
            </span>
          </div>
        </div>

        {/* Navigation items */}
        <nav className="flex flex-col w-full gap-1 px-2">
          {navItemsFiltered.map(renderNavItem)}
        </nav>
      </div>

      {/* Logout Button */}
      <div className="mb-4 px-2">
        {isExpanded ? (
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 px-3 h-10 text-muted-foreground hover:text-foreground transition-colors duration-200"
            onClick={logout}
          >
            <LogOut className="h-5 w-5 text-gray-500" />
            <span className="text-sm text-gray-500 transition-colors duration-200">
              Logout
            </span>
          </Button>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-10 w-10 p-0 text-muted-foreground hover:text-foreground mx-auto transition-colors duration-200"
                  onClick={logout}
                >
                  <LogOut className="h-5 w-5 text-gray-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Logout</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};

export default memo(Sidebar);
